"use client";

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';

import { cn } from '@/lib/utils';
import { SHOW_TEMPLATES } from '@/lib/feature-flags';
import { Button } from '@/components/ui/button';


{/* Browser chrome mockup wrapper */ }
function BrowserFrame({ url, children, className = '' }: { url: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col bg-white rounded-xl overflow-hidden border border-bloom-border/60", className)}>
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-bloom-cream/50 border-b border-bloom-border/40 flex-shrink-0">
        {/* Traffic lights */}
        <div className="flex items-center gap-1.5 pl-1">
          <div className="w-2 h-2 rounded-full bg-[#FF5F57]/80" />
          <div className="w-2 h-2 rounded-full bg-[#FFBD2E]/80" />
          <div className="w-2 h-2 rounded-full bg-[#28C840]/80" />
        </div>
        {/* URL bar */}
        {url && (
          <div className="flex-1 mx-3">
            <div className="bg-white/60 rounded-md px-3 py-1 text-[10px] font-sans font-medium text-bloom-green-deep/30 text-center truncate border border-bloom-border/30 uppercase tracking-widest">
              {url}
            </div>
          </div>
        )}
      </div>
      {/* Screenshot */}
      <div className="relative flex-1">
        {children}
      </div>
    </div>
  );
}

export function Hero() {
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -12 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1] as const,
      },
    },
  };

  return (
    <section
      className="relative min-h-[calc(100vh-44px)] xl:min-h-[calc(100vh-96px)] flex flex-col xl:flex-row overflow-hidden bg-bloom-cream"
      aria-labelledby="hero-heading"
    >
      {/* Left Column — Typography First */}
      <div className="flex-1 xl:flex-[0.55] flex flex-col justify-center pt-32 pb-20 xl:pt-12 xl:pb-0 px-7 sm:px-10 lg:px-14 xl:px-16 2xl:px-32 relative z-10">
        <motion.div
          className="flex flex-col max-w-[655px]"
          variants={containerVariants}
          initial="visible"
          animate="visible"
        >
          {/* Eyebrow */}
          <motion.span
            initial={{ opacity: 0.9, y: 3 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="text-xs font-bold tracking-[3px] text-bloom-gold-dark uppercase mt-10 mb-3 block"
          >
            Laurel Web Co.
          </motion.span>

          {/* Headline */}
          <motion.h1
            id="hero-heading"
            initial={{ opacity: 0.9, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="heading-hero mb-7"
          >
            Websites built with care, designed to <span className="accent-italic">perform.</span>
          </motion.h1>

          {/* Subheading */}
          <motion.p
            className="body-large max-w-[540px] mb-12"
            variants={itemVariants}
          >
            Custom websites for small businesses, nonprofits, HOAs, churches, and the local organizations people rely on. Accessibility-first. Built to last. Built in Georgia.
          </motion.p>

          {/* CTAs */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 sm:gap-5 mb-14"
            variants={itemVariants}
          >
            <Button asChild size="lg" className="shadow-xl">
              <Link href="/contact">Get in Touch</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link href="/services">Explore Services</Link>
            </Button>
          </motion.div>

          {/* Proof Strip */}
          <motion.div
            className="pt-10 border-t border-bloom-border/30"
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { duration: 0.8, delay: 0.8 } }
            }}
          >
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {[
                'Universal Digital Access',
                'High-Performance Static Builds',
                '18 Years Engineering Experience',
              ].map((item, i) => (
                <span key={item} className="flex items-center gap-3 label-text !text-[10px] !text-bloom-green-deep/60">
                  <span className="w-1.5 h-1.5 rounded-full bg-bloom-gold flex-shrink-0" />
                  {item}
                </span>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Mobile / Tablet — Horizontal scroll strip of all 3 browser windows */}
      <div className="block xl:hidden pb-20">
        <motion.div
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-7 sm:px-10 lg:px-14 pb-4 scrollbar-none"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {[
            { src: '/images/projects/baldie-con/homepage.webp', url: 'baldiecon.com', alt: 'Baldie Con project' },
            { src: '/images/projects/the-clock-avenue/the-clock-avenue.webp', url: 'theclockavenue.com', alt: 'The Clock Avenue project' },
            { src: '/images/projects/georgia-homeless-pets/homepage.webp', url: 'georgiahomelesspets.org', alt: 'Georgia Homeless Pets project' },
            { src: '/images/projects/other/church-site.webp', url: '', alt: 'Grace Community Chapel' },
            { src: '/images/projects/other/tree-service.webp', url: '', alt: 'Greenline Tree Service' },
            { src: '/images/projects/other/clean-service.webp', url: '', alt: 'ProClean Cleaning Co' },
            { src: '/images/projects/village-at-cobblestone/homepage.webp', url: 'villageatcobblestone.vercel.app', alt: 'Village at Cobblestone project' },
          ].map((project, i) => (
            <div
              key={project.src}
              className="flex-shrink-0 w-[82vw] sm:w-[72vw] lg:w-[56vw] snap-start rounded-xl overflow-hidden shadow-[0_16px_48px_-12px_rgba(27,67,50,0.14)]"
            >
              <BrowserFrame url={project.url}>
                <div className="relative aspect-video">
                  <Image
                    src={project.src}
                    alt={project.alt}
                    fill
                    className="object-cover object-top"
                    priority={i === 0}
                    sizes="(max-width: 768px) 82vw, (max-width: 1024px) 56vw, 100vw"
                  />
                </div>
              </BrowserFrame>
            </div>
          ))}
          {/* Trailing spacer so last card doesn't hug the edge */}
          <div className="flex-shrink-0 w-7 sm:w-10 lg:w-14" />
        </motion.div>
      </div>

      {/* Desktop xl+ — Floating stacked card composition */}
      <div className="hidden xl:flex xl:flex-1 xl:flex-[0.45] items-center justify-center p-8 2xl:p-12 overflow-visible">
        <div className="relative w-[560px] h-[680px]">

          {/* Background Mock 1 — Tree Service */}
          <motion.div
            initial={{ opacity: 0.85, y: 120, rotate: -12, x: -140 }}
            animate={{ opacity: 0.85, y: 120, rotate: -12, x: -140 }}
            whileHover={{ scale: 1.05, rotate: -8, zIndex: 30 }}
            className="absolute top-[10%] left-0 w-[70%] z-0 rounded-xl overflow-hidden shadow-lg"
          >
            <BrowserFrame url="">
              <div className="relative aspect-video">
                <Image
                  src="/images/projects/the-clock-avenue/the-clock-avenue.webp"
                  alt="The Clock Avenue"
                  fill
                  className="object-cover object-top"
                  sizes="300px"
                />
              </div>
            </BrowserFrame>
          </motion.div>

          {/* Background Mock 2 — Church */}
          <motion.div
            initial={{ opacity: 0.9, y: -100, rotate: 15, x: 140 }}
            animate={{ opacity: 0.9, y: -100, rotate: 15, x: 140 }}
            whileHover={{ scale: 1.05, rotate: 10, zIndex: 30 }}
            className="absolute bottom-[20%] right-0 w-[70%] z-0 rounded-xl overflow-hidden shadow-lg"
          >
            <BrowserFrame url="">
              <div className="relative aspect-video">
                <Image
                  src="/images/projects/other/church-site.webp"
                  alt="Grace Community Chapel"
                  fill
                  className="object-cover object-top"
                  sizes="300px"
                />
              </div>
            </BrowserFrame>
          </motion.div>

          {/* Background Mock 3 — Cleaning Co */}
          <motion.div
            initial={{ opacity: 0.8, y: -240, rotate: -5, x: -80 }}
            animate={{ opacity: 0.8, y: -240, rotate: -5, x: -80 }}
            whileHover={{ scale: 1.05, rotate: 0, zIndex: 30 }}
            className="absolute bottom-0 left-[10%] w-[65%] z-0 rounded-xl overflow-hidden shadow-md"
          >
            <BrowserFrame url="">
              <div className="relative aspect-video">
                <Image
                  src="/images/projects/other/clean-service.webp"
                  alt="ProClean Cleaning Co"
                  fill
                  className="object-cover object-top"
                  sizes="280px"
                />
              </div>
            </BrowserFrame>
          </motion.div>

          {/* Tertiary card — Village at Cobblestone — pops in FIRST (background) */}
          <motion.div
            initial={{ opacity: 0.28, y: 0 }}
            animate={{ opacity: 0.28, y: 0 }}
            className="absolute -top-[2%] right-[6%] w-[68%] shadow-sm z-0 rounded-xl overflow-hidden"
          >
            <BrowserFrame url="villageatcobblestone.vercel.app">
              <div className="relative aspect-video">
                <Image
                  src="/images/projects/village-at-cobblestone/homepage.webp"
                  alt="Village at Cobblestone project"
                  fill
                  className="object-cover object-top"
                  sizes="(max-width: 1280px) 100vw, (max-width: 1536px) 320px, 380px"
                />
              </div>
            </BrowserFrame>
          </motion.div>

          {/* Secondary card — Georgia Homeless Pets — pops in SECOND (middle) */}
          <motion.div
            initial={{ opacity: 1, y: 0, rotate: 5 }}
            animate={{ opacity: 1, y: 0, rotate: 5 }}
            style={{ willChange: 'transform' }}
            className="absolute top-[42%] right-[0%] w-[82%] shadow-[0_24px_48px_-12px_rgba(27,67,50,0.12)] z-10 opacity-75 rounded-xl overflow-hidden"
          >
            <BrowserFrame url="georgiahomelesspets.org">
              <div className="relative aspect-video">
                <Image
                  src="/images/projects/georgia-homeless-pets/homepage.webp"
                  alt="Georgia Homeless Pets project"
                  fill
                  className="object-cover object-top"
                  sizes="(max-width: 1280px) 100vw, (max-width: 1536px) 380px, 460px"
                />
              </div>
            </BrowserFrame>
          </motion.div>

          {/* Primary card — Baldie Con — pops in LAST (front, hero) */}
          <motion.div
            initial={{ opacity: 1, y: 0, rotate: -3 }}
            animate={{ opacity: 1, y: 0, rotate: -3 }}
            whileHover={{ scale: 1.015 }}
            style={{ willChange: 'transform' }}
            className="absolute top-[10%] left-[2%] w-[92%] shadow-[0_40px_80px_-20px_rgba(27,67,50,0.18)] z-20 rounded-xl overflow-hidden"
          >
            <BrowserFrame url="baldiecon.com">
              <div className="relative aspect-video">
                <Image
                  src="/images/projects/baldie-con/homepage.webp"
                  alt="Baldie Con project"
                  fill
                  className="object-cover object-top"
                  priority
                  sizes="(max-width: 1280px) 100vw, 45vw"
                />
              </div>
            </BrowserFrame>
          </motion.div>

          {/* Accent glow */}
          <div className="absolute -bottom-16 right-0 w-72 h-72 bg-bloom-gold/10 blur-[100px] rounded-full pointer-events-none" />
        </div>
      </div>
    </section>
  );
}
